import { VoiceAgentAvatar, VoiceAgentControls } from "@/components/VoiceAgent";
import { GenieChatInputWeb } from "@/pages/agent-interfaces/live-agent/components/GenieChatInputWeb";
import { GenieTextChatOverlayWeb } from "@/pages/agent-interfaces/live-agent/components/GenieTextChatOverlayWeb";
import { DEFAULT_BRANDING } from "@/types/ui/branding";

interface VoiceControlsSectionProps {
  isMobile: boolean;
  showChatModal: boolean;
  speaking: boolean;
  listening: boolean;
  thinking: boolean;
  connected: boolean;
  isConnecting: boolean;
  messages: any[];
  inlineMessage: string;
  onChangeInlineMessage: (message: string) => void;
  onSendMessage: () => void;
  onToggleConversation: () => Promise<void>;
  attachments: any[];
  onRemoveAttachment: (id: string) => void;
  onOpenFilePicker: () => void;
  isTextOnlyMode: boolean;
  isAllAudioMuted: boolean;
  onToggleAllAudioMute: () => void;
  startedFromText: boolean;
  onCloseOverlay: () => void;
  onEndChat: () => void;
}

export const VoiceControlsSection = ({
  isMobile,
  showChatModal,
  speaking,
  listening,
  thinking,
  connected,
  isConnecting,
  messages,
  inlineMessage,
  onChangeInlineMessage,
  onSendMessage,
  onToggleConversation,
  attachments,
  onRemoveAttachment,
  onOpenFilePicker,
  isTextOnlyMode,
  isAllAudioMuted,
  onToggleAllAudioMute,
  startedFromText,
  onCloseOverlay,
  onEndChat,
}: VoiceControlsSectionProps) => {
  return (
    <>
      {/* Avatar or text input - replaces each other */}
      {!showChatModal ? (
        <div className="my-6 md:my-0">
          <VoiceAgentAvatar
            brandColor={DEFAULT_BRANDING.primaryColor}
            size={isMobile ? 280 : 320}
            onClick={onToggleConversation}
            speaking={speaking}
            listening={listening}
            thinking={thinking}
            isConnecting={isConnecting}
            connected={connected}
            customBranding={{
              primaryColor: DEFAULT_BRANDING.primaryColor,
              customAvatarUrl: "/genie-mascots/role/concierge.png",
            }}
          />
        </div>
      ) : (
        <div style={{ marginTop: 12, width: "100%", maxWidth: 460 }}>
          <GenieTextChatOverlayWeb
            brandColor={DEFAULT_BRANDING.primaryColor}
            messages={messages.map((msg: any) => ({
              message: msg.message,
              isUser: msg.role === "user",
              timestamp: new Date(msg.timestamp),
              imageAttachments: msg.imageAttachments,
            }))}
            inlineMessage={inlineMessage}
            onChangeInlineMessage={onChangeInlineMessage}
            onSend={onSendMessage}
            isConnecting={isConnecting}
            isSpeaking={speaking}
            isTextOnlyMode={isTextOnlyMode}
            isAllAudioMuted={isAllAudioMuted}
            onToggleAllAudioMute={onToggleAllAudioMute}
            startedFromText={startedFromText}
            onCloseOverlay={onCloseOverlay}
            onEndChat={onEndChat}
            attachments={attachments.map((att) => ({
              id: att.id,
              previewUrl: att.previewUrl,
              status: att.status,
              analysisText: att.analysisText,
            }))}
            onRemoveAttachment={onRemoveAttachment}
            onOpenFilePicker={onOpenFilePicker}
          />
        </div>
      )}

      {/* Voice Agent Controls - hidden when in chat modal */}
      {!showChatModal && (
        <VoiceAgentControls
          connected={connected}
          isConnecting={isConnecting}
          onToggle={onToggleConversation}
          brandColor={DEFAULT_BRANDING.primaryColor}
          buttonText={{
            start: "Begin Setup",
            end: "End Setup & Analyze",
          }}
          className="mt-3 sm:mt-4 md:mt-6"
        />
      )}

      {/* Simple text input below controls before conversation starts */}
      {!showChatModal && (
        <div style={{ marginTop: 12, width: "100%", maxWidth: 460 }}>
          <GenieChatInputWeb
            value={inlineMessage}
            onChange={onChangeInlineMessage}
            onSend={onSendMessage}
            attachments={attachments.map((att, index) => ({
              id: att.id,
              name: `Attachment ${index + 1}`,
              previewUrl: att.previewUrl,
              status: att.status,
              analysisText: att.analysisText,
            }))}
            onRemoveAttachment={onRemoveAttachment}
            onOpenFilePicker={onOpenFilePicker}
          />
        </div>
      )}
    </>
  );
};
